import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled, { keyframes } from "styled-components/macro";
import { ChevronLeft, ChevronRight } from "react-feather";
import { getModelFromConfig } from "../../utilities/configUtils";
import { buildStatelessConfigImageUrl } from "../../utilities/configImageUrl";
import { getAccount, getLocalization, getModelId } from "../../store/sagas/selectors";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";
import FavoriteIcon from "../../icons/Favorite.svg";
import CustomScrollbar from "../CustomScrollbar/CustomScrollbar";

const IMAGE_TOTAL_SIZE = 175;

const onLoadAnimation = (bottom: string) => keyframes`
    0% {bottom: 95px; opacity:0;}
    100% {bottom: ${bottom}; opacity:1;}
`;

const onLoadAnimationMobile = keyframes`
    0% {opacity:0;}
    100% {opacity:1;}
`;

const FavoritContainer = styled.div<FavoriteContainerProps>`
    display: block;
    position: absolute;
    bottom: ${props => (props.isMobile ? "" : "115px")};
    z-index: 5;
    width: ${props => (props.isMobile ? "calc(100% - 20px)" : "100%")};
    height: ${props => (props.isMobile ? "100%" : "150px")};
    ${props => (props.isMobile ? "padding: 0px 10px" : "")};
    display: flex;
    flex-direction: ${props => (props.isMobile ? "column" : "row")};
    justify-content: center;
    align-items: center;
    animation-name: ${props => (props.isMobile ? onLoadAnimationMobile : onLoadAnimation(props.bottomMargin))};
    animation-duration: 0.5s;
    animation-timing-function: ease-in-out;
    opacity: 1;
    ${({ theme }) => theme.bp.xxxl`
        transform: scale(1.4, 1.4);
        transform-origin: bottom center;
        bottom: 145px;
    `}
`;

const Favorite = styled.img<FavoriteProps>`
    max-height: ${props => (props.isMobile ? "200px" : "90px")};
    overflow: hidden;
    outline: ${props => (props.selected ? "Thick solid var(--primary-color)" : "thin solid white")};
    transition: all 0.2s ease-in-out;
    object-fit: contain;
    margin: ${props => (props.isMobile ? "5px" : "10px")};
    max-width: calc(100% - 26px);
    box-shadow: ${props => (props.selected ? "0px 6px 12px #515151" : "0px 2px 8px #515151")};
    @media (hover) {
        &:hover {
            outline: solid thick var(--primary-hover-color);
            cursor: pointer;
            box-shadow: 0px 6px 12px #515151;
        }
    }
`;

const FavoriteSlideContainer = styled.div<FavoriteContainerProps>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    height: 90%;
    ${props => (props.isMobile ? "width: calc(100% - 16px);" : "")}
`;

const FavoriteIconContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const SlideIconContainer = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    bottom: -6px;
`;

const NumSlidesIcon = styled.div<CurrentSectionProps>`
    width: 8px;
    height: 8px;
    border-radius: 20px;
    border: solid 2px white;
    box-shadow: 0px 0px 4px rgb(0 0 0 / 30%);
    background-color: ${props => (props.selected ? "rgba(255, 255, 255, 1)" : "rgba(50, 50, 50, 0.3)")};
    margin: 30px 10px 0px 10px;
    transition: all 0.3s ease-in-out;
    @media (hover) {
        &:hover {
            cursor: ${props => (props.selected ? "default" : "pointer")};
            background-color: rgba(255, 255, 255, 1);
        }
    }
`;

const LayoutArrowButtons = styled.div<ArrowProps>`
    width: ${props => (props.isMobile ? "40px" : "100px")};
    height: ${props => (props.isMobile ? "40px" : "100px")};
    border-radius: 100px;
    color: white;
    margin: ${props => (props.isMobile ? "0px" : "0px 10px")};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    filter: drop-shadow(0px 1px 2px #2e2e2e) opacity(70%);
    transition: all 0.3s ease-in-out;
    @media (hover) {
        &:hover {
            cursor: pointer;
            filter: drop-shadow(0px 1px 2px #2e2e2e) opacity(100%);
        }
    }
`;

const ToggleFavorite = styled.div<ToggleFavoriteProps>`
    width: 20px;
    height: 20px;
    position: absolute;
    right: 13px;
    top: ${props => (props.isMobile ? "5px" : "9px")};
    padding: 4px;
    border-radius: 20px;
    @media (hover) {
        &:hover {
            cursor: pointer;
        }
    }
`;

const SvgImg = styled.div<IconProps>`
    width: 20px;
    height: 20px;
    filter: drop-shadow(0px 2px 2px rgb(0 0 0 / 0.4));
    svg {
        transition: all 0.4s ease-in-out;
        fill: currentColor;
        color: ${props => (props.active ? "var(--light-active-color)" : "var(--primary-color)")};
        @media (hover) {
            &:hover {
                color: ${props => (props.active ? "var(--primary-hover-color)" : "var(--light-hover-color)")};
            }
        }
    }
`;

interface FavoriteProps {
    selected: Boolean;
    isMobile: Boolean;
}

interface CurrentSectionProps {
    selected: Boolean;
}

interface ToggleFavoriteProps {
    selected: Boolean;
    isMobile: Boolean;
}

interface IconProps {
    active: Boolean;
}

interface ArrowProps {
    isMobile: Boolean;
}

interface FavoriteContainerProps {
    isMobile: Boolean;
    bottomMargin: string;
}

export const FavoritesList = ({
    favorites,
    onClick,
    toggleFavorites,
    removedFavorites,
    currentConfig,
}: {
    favorites: string[];
    onClick: Function;
    toggleFavorites: Function;
    removedFavorites: string[];
    currentConfig: string;
}) => {
    const initialImage = favorites.indexOf(currentConfig) > -1 ? currentConfig : favorites[0];
    const [activeFavorite, setActiveFavorite] = useState(initialImage);
    const [currentSection, setCurrentSection] = useState(0);
    const breakpoints = useBreakpoint();
    const modelId = useSelector(getModelId);
    const account = useSelector(getAccount);
    const market = useSelector(getLocalization);
    const isMobile = breakpoints.xxs || breakpoints.xs || breakpoints.sm;
    const scale = window.devicePixelRatio;
    const imgHeight = isMobile ? 200 * scale : 180;
    const statelessImageParams = {
        scaledHeight: imgHeight,
        ext: "jpg" as const,
        accountId: account?.id,
        market,
    };
    const highResBottom = breakpoints.xxxl ? "145px" : "115px";
    const currentFavorites = favorites.filter(v => getModelFromConfig(v) === modelId);

    let favoriteinSection: string[][];

    const tempArrr = [];
    tempArrr.push(currentFavorites);
    favoriteinSection = tempArrr;

    let showArrows = false;

    const isHighResModifier = breakpoints.xxxl ? 0.5 : 0.7;

    // Fixed width right now. But we dont have any width of any image to go on.
    if (currentFavorites.length * IMAGE_TOTAL_SIZE > window.innerWidth * isHighResModifier) {
        const sectionSize = Math.floor((window.innerWidth * isHighResModifier) / IMAGE_TOTAL_SIZE);

        if (currentFavorites.length > sectionSize) {
            showArrows = true;
            const tempArr = [];
            for (let i = 0; i < currentFavorites.length; i += sectionSize) {
                tempArr.push(currentFavorites.slice(i, i + sectionSize));
            }
            favoriteinSection = tempArr;
        }
    }

    const setActive = (config: string) => {
        setActiveFavorite(config);
        onClick(config);
    };

    const setSection = (value: number) => {
        setCurrentSection(value);
    };

    const moveSectionLeft = (value: number) => {
        if (currentSection <= 0) {
            setSection(favoriteinSection.length - 1);
        } else {
            setSection(currentSection - value);
        }
    };

    const moveSectionRight = (value: number) => {
        if (currentSection >= favoriteinSection.length - 1) {
            setSection(0);
        } else {
            setSection(currentSection + value);
        }
    };
    return (
        <FavoritContainer isMobile={isMobile} bottomMargin={highResBottom}>
            {isMobile ? (
                <>
                    <CustomScrollbar maxHeight="100%">
                        <FavoriteSlideContainer isMobile={isMobile} bottomMargin={highResBottom}>
                            {currentFavorites.map(f => {
                                return (
                                    <FavoriteIconContainer key={f}>
                                        <ToggleFavorite
                                            key={removedFavorites.indexOf(f)}
                                            selected={removedFavorites.indexOf(f) > -1}
                                            onClick={() => toggleFavorites(f)}
                                            isMobile={isMobile}
                                        >
                                            <SvgImg active={removedFavorites.indexOf(f) > -1}>
                                                <FavoriteIcon />
                                            </SvgImg>
                                        </ToggleFavorite>
                                        <Favorite
                                            key={f}
                                            selected={activeFavorite === f}
                                            onClick={() => setActive(f)}
                                            src={buildStatelessConfigImageUrl(f, statelessImageParams)}
                                            isMobile={isMobile}
                                        />
                                    </FavoriteIconContainer>
                                );
                            })}
                        </FavoriteSlideContainer>
                    </CustomScrollbar>
                </>
            ) : (
                <>
                    {showArrows ? (
                        <LayoutArrowButtons isMobile={isMobile} onClick={() => moveSectionLeft(1)}>
                            <ChevronLeft size={44} color="#fff" />
                        </LayoutArrowButtons>
                    ) : null}
                    <FavoriteSlideContainer isMobile={isMobile} bottomMargin={highResBottom}>
                        {favoriteinSection[currentSection].map(f => {
                            return (
                                <FavoriteIconContainer key={f}>
                                    <ToggleFavorite
                                        key={removedFavorites.indexOf(f)}
                                        selected={removedFavorites.indexOf(f) > -1}
                                        onClick={() => toggleFavorites(f)}
                                        isMobile={isMobile}
                                    >
                                        <SvgImg active={removedFavorites.indexOf(f) > -1}>
                                            <FavoriteIcon />
                                        </SvgImg>
                                    </ToggleFavorite>
                                    <Favorite
                                        key={f}
                                        selected={activeFavorite === f}
                                        onClick={() => setActive(f)}
                                        src={buildStatelessConfigImageUrl(f, statelessImageParams)}
                                        isMobile={isMobile}
                                    />
                                </FavoriteIconContainer>
                            );
                        })}
                    </FavoriteSlideContainer>
                    {showArrows ? (
                        <LayoutArrowButtons isMobile={isMobile} onClick={() => moveSectionRight(1)}>
                            <ChevronRight size={44} color="#fff" />
                        </LayoutArrowButtons>
                    ) : null}

                    {favoriteinSection.length > 1 ? (
                        <SlideIconContainer>
                            {favoriteinSection.map((g, index) => (
                                <NumSlidesIcon
                                    selected={index === currentSection}
                                    onClick={() => setSection(index)}
                                    key={g[0]}
                                />
                            ))}
                        </SlideIconContainer>
                    ) : null}
                </>
            )}
        </FavoritContainer>
    );
};
